import React from 'react';
import { Menu, Transition } from '@headlessui/react';
import { EllipsisHorizontalIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { usePremium } from '../Subscription/PremiumProvider';
import { Zap } from 'lucide-react';

function PersonalWorkspaceCard({ 
  workspace, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onClick,
  isEditing,
  editedName,
  onEditNameChange,
  onSaveEdit,
  isLoading 
}) {
  const { isPremium, openUpgradeModal } = usePremium();

  const handleDuplicateClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isPremium()) {
      openUpgradeModal();
      return;
    }
    onDuplicate(workspace);
  };

  return (
    <div
      onClick={() => !isEditing && onClick(workspace)}
      className="bg-white pt-3 pb-6 px-4 sm:px-6 rounded-xl border-2 border-neutral-900 hover:shadow-md transition-all duration-300 cursor-pointer"
    >
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        {isEditing ? (
          <form 
            onSubmit={onSaveEdit}
            className="flex-1 mr-2"
            onClick={e => e.stopPropagation()}
          >
            <input
              type="text"
              value={editedName}
              onChange={(e) => onEditNameChange(e.target.value)}
              onBlur={onSaveEdit}
              className="w-full px-3 py-1.5 text-sm font-medium border-2 border-neutral-900 rounded-lg"
              autoFocus
            />
          </form>
        ) : (
          <h3 className="font-medium text-neutral-900 truncate pr-2 text-sm sm:text-base">
            {workspace.name}
          </h3>
        )}
        
        <Menu as="div" className="relative">
          <Menu.Button 
            className="p-1 sm:p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisHorizontalIcon className="h-4 w-4 sm:h-5 sm:w-5 text-neutral-600" />
          </Menu.Button>
          
          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items 
              className="absolute right-0 mt-1 w-44 sm:w-48 bg-white rounded-xl border-2 border-neutral-900 shadow-lg z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-neutral-100' : ''
                      } flex items-center w-full px-4 py-2 text-sm text-neutral-900`}
                      onClick={() => onEdit(workspace)}
                    >
                      <PencilIcon className="h-4 w-4 mr-3 text-neutral-600" />
                      Edit Name
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-neutral-100' : ''
                      } flex items-center justify-between w-full px-4 py-2 text-sm text-neutral-900`}
                      onClick={handleDuplicateClick}
                      disabled={isLoading}
                    >
                      <div className="flex items-center">
                        <DocumentDuplicateIcon className="h-4 w-4 mr-3 text-neutral-600" />
                        {isLoading ? 'Duplicating...' : 'Duplicate'}
                      </div>
                      {!isPremium() && (
                        <div className="flex items-center">
                          <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
                        </div>
                      )}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-neutral-100' : ''
                      } flex items-center w-full px-4 py-2 text-sm text-cranberry-700`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(workspace);
                      }}
                    >
                      <TrashIcon className="h-4 w-4 mr-3 text-cranberry-700" />
                      Delete
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
      <div className="flex items-center justify-between mt-auto">
        <div className="text-xs text-blue-600">
          {workspace.basketCount || 0} {workspace.basketCount === 1 ? 'basket' : 'baskets'}
        </div>
        <div className="text-xs text-neutral-500 italic">
          {formatDistanceToNow(new Date(workspace.created_at), { addSuffix: true })}
        </div>
      </div>
    </div>
  );
}

export default PersonalWorkspaceCard; 